import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header/Header';
import Dashboard from './pages/Dashboard/Dashboard';
import LeadsSalvos from './pages/LeadsSalvos/LeadsSalvos';
import Integracoes from './pages/Integracoes/Integracoes';
import AuthPage from './pages/Auth/AuthPage';
import UpdatePasswordPage from './pages/Auth/UpdatePasswordPage';
import ProfilePage from './pages/Profile/ProfilePage';
import SettingsPage from './pages/Settings/SettingsPage';
import HelpPage from './pages/Help/HelpPage';
import AIPage from './pages/AI/AIPage';
import BulkSendAgentConfigPage from './pages/AI/BulkSendAgentConfigPage';
import WhatsappAgentConfigPage from './pages/AI/WhatsappAgentConfigPage';
import DocumentationPageWrapper from './pages/Documentation/index';
import FAQPageWrapper from './pages/Documentation/FAQWrapper';
import GuidePageWrapper from './pages/Documentation/GuideWrapper';
import LegalPage from './pages/Legal/LegalPage';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import { useAuth } from './hooks/useAuth';
import { Toaster, toast } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import SubscriptionBlockModal from './components/SubscriptionBlockModal/SubscriptionBlockModal';
import { TourProvider } from './contexts/TourContext';
import Tour from './components/Tour/Tour';
import TourAutoStart from './components/Tour/TourAutoStart';
import ImpersonationBanner from './components/ImpersonationBanner/ImpersonationBanner';
import { getImpersonationInfo } from './services/adminService';


function App() {
  const { user, isLoading, profile } = useAuth();

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    if (!isLoading && !user && !subscriptionChecked) {
      const lastStatus = localStorage.getItem('last_subscription_status');

      if (lastStatus && lastStatus !== 'pro') {
        toast.error('Sua assinatura não está ativa. Por favor, verifique seu status de pagamento na Hotmart.', { duration: 6000 });
        localStorage.removeItem('last_subscription_status');
      }
      setSubscriptionChecked(true);
    }

    if (user && profile) {
      localStorage.setItem('last_subscription_status', profile.subscription_status);
    }

  }, [user, isLoading, profile, subscriptionChecked]);


  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/update-password" element={<UpdatePasswordPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="*" element={<AuthPage />} />
        </Routes>
      </>
    );
  }

  const impersonationInfo = getImpersonationInfo();
  // Check if user has valid subscription (basic or pro) - Admin impersonando tem bypass para poder inspecionar a conta
  const hasValidSubscription = impersonationInfo.isImpersonating || profile?.subscription_status === 'basic' || profile?.subscription_status === 'pro';

  return (
    <ErrorBoundary>
      <TourProvider>
        <div className="min-h-screen bg-background-light dark:bg-background-dark transition-colors duration-300">
          <Toaster position="top-right" />

          {/* Show subscription block modal if subscription is invalid */}
          <SubscriptionBlockModal isOpen={!hasValidSubscription} />

          {/* Tour Overlay and Tooltip */}
          <Tour />
          <TourAutoStart />

          <motion.div
            key={darkMode ? 'dark' : 'light'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative flex h-auto min-h-screen w-full flex-col"
          >
            {/* Banner fixo superior quando estiver acessando a conta de outro usuário */}
            <ImpersonationBanner />
            <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/leads-salvos" element={<LeadsSalvos />} />
                <Route path="/integracoes" element={<Integracoes />} />
                <Route path="/ia" element={<AIPage />} />
                <Route path="/ia/bulk-send" element={<BulkSendAgentConfigPage />} />
                <Route path="/ia/whatsapp-agent" element={<WhatsappAgentConfigPage />} />
                <Route path="/perfil" element={<ProfilePage />} />
                <Route path="/configuracoes" element={<SettingsPage />} />
                <Route path="/documentacao" element={<DocumentationPageWrapper />} />
                <Route path="/documentacao/faq" element={<FAQPageWrapper />} />
                <Route path="/documentacao/guia" element={<GuidePageWrapper />} />
                <Route path="/ajuda" element={<HelpPage />} />
                <Route path="/legal" element={<LegalPage />} />
                <Route path="/update-password" element={<UpdatePasswordPage />} />
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              </Routes>
            </main>
          </motion.div>
        </div>
      </TourProvider>
    </ErrorBoundary>
  );
}

export default App;